import React, { useMemo, useState } from 'react';
import classnames from 'classnames';

import { can } from '../can';
import { routes } from '../routes';
import { getCountryName } from '../utils/index';

import api from '../utils/api';
import { useQuery } from '../utils/query';

import { lockerStatusLabels, lockerStatusColors, lockerConnectionLabels, lockerConnectionColors, doorTypeLabels } from '../constants';

import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Toggle from '../components/common/Toggle';
import Link from '../components/common/Link';

import { List, Header, Body, Row, Cell } from '../components/common/List';
import { modeLabels } from '../constants';

import { Loading } from './PageStatus';

import css from './Lockers.css';
import { FiInfo, FiShuffle } from 'react-icons/fi';
import Modal from '../components/common/Modal';


export const Lockers = (props) => {
  const query = useQuery(props.location, props.history);
  const { user } = api.auth();
  const lockerIndexQuery = useMemo(() => {
    const currentQuery = { ...query.obj };
    const search = typeof currentQuery.search === 'string' ? currentQuery.search.trim() : '';

    if (!search || search.length < 3) {
      delete currentQuery.search;
    } else {
      currentQuery.search = search;
    }

    return currentQuery;
  }, [query.obj]);

  const { index, actions } = api.lockers.index(lockerIndexQuery);
  const [modalSpecs, setModalSpecs] = useState(null);
  const [lockerName, setLockerName] = useState('');

  return (
    <main className={css.root}>
      <Modal open={!!modalSpecs} onClose={() => setModalSpecs(null)}>
        <h3>Especificaciones del locker: {lockerName}</h3>
        {modalSpecs ? (
          <div className={css.modalSpecsContainer}>
            {modalSpecs.eLockerImage && (
              <div className={css.modalSpecsSection}>
                <strong>Imagen del eLocker</strong>
                <pre className={css.modalSpecsPre}>
                  {modalSpecs.eLockerImage.digest.split('   ').map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </pre>
              </div>
            )}

            {modalSpecs.unameA && typeof modalSpecs.unameA === 'string' && (
              <div className={css.modalSpecsSection}>
                <strong>uname -a</strong>
                <div className={css.modalSpecsPre} style={{ background: 'none', color: '#222', padding: 0 }}>
                  {(() => {
                    const uname = modalSpecs.unameA.trim();
                    const parts = uname.split(/\s+/);
                    if (parts.length > 4) {
                      return (
                        <table style={{ width: '100%', fontSize: 13 }}>
                          <tbody>
                            <tr><td><b>OS</b></td><td>{parts[0]}</td></tr>
                            <tr><td><b>Host</b></td><td>{parts[1]}</td></tr>
                            <tr><td><b>Kernel</b></td><td>{parts[2]}</td></tr>
                            <tr><td><b>Build</b></td><td>{parts[3]}</td></tr>
                            <tr><td><b>Arch/Rest</b></td><td>{parts.slice(4).join(' ')}</td></tr>
                          </tbody>
                        </table>
                      );
                    } else {
                      return <pre className={css.modalSpecsPre}>{uname}</pre>;
                    }
                  })()}
                </div>
              </div>
            )}
            {modalSpecs.crontab && typeof modalSpecs.crontab === 'string' && (
              <div className={css.modalSpecsSection}>
                <strong>crontab</strong>
                <pre className={css.modalSpecsPre}>
                  {modalSpecs.crontab.trim()}
                </pre>
              </div>
            )}
            {modalSpecs.spaceLeft && (
              <div className={css.modalSpecsSection}>
                <strong>Espacio libre</strong>
                <pre className={css.modalSpecsPre}>
                  {modalSpecs.spaceLeft.trim()}
                </pre>
              </div>
            )}

            {
              modalSpecs.lastInit && (
                <div className={css.modalSpecsSection}>
                  <strong>Última inicialización</strong>
                  <pre className={css.modalSpecsPre}>
                    {modalSpecs.lastInit.trim()}
                  </pre>
                </div>
              )
            }

            {modalSpecs.lastBackup && (
              <div className={css.modalSpecsSection}>
                <strong>Último backup</strong>
                <pre className={css.modalSpecsPre}>
                  {modalSpecs.lastBackup.trim()}
                </pre>
              </div>
            )}
            {modalSpecs.lastAutoUpdate && (
              <div className={css.modalSpecsSection}>
                <strong>Última actualización automática</strong>
                <pre className={css.modalSpecsPre}>
                  {modalSpecs.lastAutoUpdate.trim()}
                </pre>
              </div>
            )}

            {
              modalSpecs.grafanaServiceRunning !== undefined && (
                <div className={css.modalSpecsSection}>
                  <strong>Servicio de Grafana</strong>
                  <pre className={css.modalSpecsPre}>
                    {modalSpecs.grafanaServiceRunning ? 'En ejecución' : 'No se está ejecutando'}
                  </pre>
                </div>
              )
            }

            {modalSpecs.actualTime && (
              <div className={css.modalSpecsSection}>
                <strong>Hora actualización</strong>
                <pre className={css.modalSpecsPre}>
                  {modalSpecs.actualTime.trim()}
                </pre>
              </div>
            )}
          </div>
        ) : (
          <pre className={css.modalSpecsPreFallback}>
            {modalSpecs ? JSON.stringify(modalSpecs, null, 2) : ''}
          </pre>
        )}
      </Modal>
      <PageHeader title="Lockers">
        {can(user, 'create', 'lockers') && (
          <Button noMargin to={routes('newLocker')}>
            Nuevo
          </Button>
        )}
      </PageHeader>
      <div className={css.toolBar}>
        <Input
          className={css.search}
          label="Buscar por nombre:"
          name="search"
          onChange={query.set}
          value={query.get('search')}
          note="Ingresa al menos 3 caracteres"
          placeholder="Nombre del locker"
        />
        <Toggle
          label="Solo lockers activos"
          onChange={(n, chk) => query.set(n, chk ? 'active' : null)}
          name={'status'}
          value={query.get('status') || false}
        />
        <Toggle
          label="Solo lockers online"
          onChange={(n, chk) => query.set(n, chk ? true : false)}
          name={'online'}
          value={query.get('online') == 'true' || query.get('online') || false}
        />
      </div>
      {index.loaded ? (
        <List className={css.root}>
          <Header>
            <Cell head>ID</Cell>
            <Cell head>Nombre</Cell>
            <Cell head>Modo - Tipo</Cell>
            <Cell head>Ubicación</Cell>
            <Cell head>País</Cell>
            <Cell head>Puertas</Cell>
            <Cell head>Estado</Cell>
            <Cell head>Conexión</Cell>
          </Header>
          <Body>
            {index.data.toJS().map((locker, index) => {
              const to = routes('locker', { id: locker.id });
              const isUnregistered = locker.status == 'unregistered';
              const prefs = locker.preferences || {};

              const labelClass = {
                [css['border-warning']]: prefs.mode === 'housing',
                [css['border-purple']]: prefs.mode === 'custody',
                [css['border-enabled']]: prefs.mode !== 'housing' && prefs.mode !== 'custody',
              };
              const country = getCountryName(locker.locationCountryCode);
              const doors = locker.doorsInfo.find((r) => r.status == 'active') || {};
              const doorsAvailable = doors.doorsTotal - doors.doorsReserved - doors.doorsOccupied;
              const reservationCapacity = prefs.enableUnlimitedReservations ? 'Ilimitada' : doors.doorsTotal - doors.doorsOccupied - doors.doorsReserved;
              const doorsReservedMessage = doors.doorsReserved ? `${doors.doorsReserved} Reservadas ` : ' Sin reservas';
              const modeLabel = modeLabels(prefs.mode) || modeLabels('normal');
              const doorTypeLabel = doorTypeLabels(prefs.doorType) || doorTypeLabels('serial');

              let incongruencesCheckIcon = null;
              if (prefs.enableIncongruencesCheck && user.role === 'admin') {
                incongruencesCheckIcon = (
                  <span
                    className={classnames(css.incongruencesCheckIcon)}
                    title="Chequear incongruencias activado"
                  >
                    <FiShuffle />
                  </span>
                );
              }

              let specsHoverIcon = null;
              if (locker.extras?.specs && user.role === 'admin') {
                specsHoverIcon = (
                  <span
                    className={css.specsHoverIcon}
                    title="Ver especificaciones"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setModalSpecs(locker.extras.specs);
                      setLockerName(locker.name);
                    }}
                  >
                    <FiInfo />
                  </span>
                );
              }

              return (
                <Row key={`lockers-row-${index}`}>
                  <Cell>
                    <Link to={to}>{locker.id}</Link>
                  </Cell>
                  <Cell className={css.name}>
                    <Link to={to}>{locker.name}</Link>
                    {specsHoverIcon}
                    &nbsp;
                    {incongruencesCheckIcon}
                  </Cell>
                  <Cell>
                    <Link to={to}>
                      {prefs.mode ? (
                        <span
                          className={classnames(css.optionType, labelClass)}
                          title={modeLabels(prefs.mode)}
                        >
                          {`${modeLabel}${doorTypeLabel ? ` - ${doorTypeLabel}` : ''}`}
                        </span>
                      ) : (
                        <span
                          className={classnames(css.optionType, css['border-enabled'])}
                          title={modeLabels('normal')}
                        >
                          {`${modeLabel}${doorTypeLabel ? ` - ${doorTypeLabel}` : ''}`}
                        </span>
                      )}
                    </Link>
                  </Cell>
                  <Cell>{locker.locationName}</Cell>
                  <Cell>{country}</Cell>
                  <Cell>
                    {doors.doorsTotal ? (
                      <>
                        <small>
                          {doorsAvailable}/{doors.doorsTotal} Disponibles
                        </small>
                        <div
                          className={css.usageBar}
                          style={{
                            width: 100, marginBottom: 5,
                          }}>
                          <div
                            className={css.inner}
                            style={{
                              width: (100 * doorsAvailable) / doors.doorsTotal,
                            }}
                          />
                        </div>
                        <small>{doorsReservedMessage}</small>&nbsp;|&nbsp;<small>{doors.doorsOccupied} Ocupadas</small>

                      </>
                    ) : (
                      <small>{'Sin puertas creadas'}</small>
                    )}
                  </Cell>
                  <Cell
                    style={{
                      width: 100,
                      color: lockerStatusColors(locker.status),
                    }}
                    className={css.status}>
                    {lockerStatusLabels(locker.status)}
                  </Cell>
                  <Cell>
                    <span
                      className={css.dot}
                      style={{
                        backgroundColor: lockerConnectionColors(
                          isUnregistered ? 'unregistered' : locker.online ? 'online' : 'offline'
                        ),
                      }}
                    />
                    {locker.status == lockerConnectionLabels(locker.online ? 'online' : 'offline')}
                  </Cell>
                </Row>
              );
            })}
          </Body>
        </List>
      ) : index.loading ? (
        <Loading compact />
      ) : (
        <p>{index.error}</p>
      )}
      {index.pageInfo && <Pagination {...index.pageInfo.toJS()} onChange={query.setPage} />}


    </main>
  );
};

export default Lockers;
